import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, Pagination, Table } from 'semantic-ui-react';

import { getActivityStatus } from '../../../utils';

import './ActivityListTable.scss';

export const hasLinkedActivities = (item) =>
  Array.isArray(item.linkedActivities) && item.linkedActivities.length > 0;

/**
 * Table for the Managed / Invited activity lists (TAK-2036).
 *
 * A program row that arrives with `linkedActivities` is rendered as its own
 * block, marked by a bar down its left edge in the program's status colour,
 * with its linked activities listed underneath. Every program (top-level or
 * nested) starts collapsed, showing only the activities the viewer runs — or
 * that contain one the viewer runs — and a toggle to show all of them when
 * any are hidden. Everything else renders as a plain row.
 *
 * Also used by the Management Summary (TAK-2081): `getRowHref` may return
 * null for a row that can't be opened (no click, no pointer),
 * `openInNewTab` opens rows in a new browser tab instead of navigating, and
 * `linkedMode="all"` makes every program collapsible regardless of who runs
 * its activities (collapsed until opened, then showing all of them).
 */

const ActivityListTable = ({
  columns,
  data,
  getRowHref,
  currentUserId,
  page,
  noOfPages,
  onPageChange,
  openInNewTab = false,
  linkedMode = 'mine',
  getRowClassName = null,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpanded = (id) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const isOwn = (item) => Number(item.owner?.id) === Number(currentUserId);
  // the viewer runs this activity, or something nested inside it
  const involvesViewer = (item) =>
    isOwn(item) || (item.linkedActivities || []).some(involvesViewer);
  const showAll = linkedMode === 'all';
  const hasHiddenByDefault = (item) =>
    hasLinkedActivities(item) &&
    (showAll || item.linkedActivities.some((child) => !involvesViewer(child)));
  const visibleLinked = (item) => {
    if (expandedIds.has(item.id)) return item.linkedActivities;
    return showAll ? [] : item.linkedActivities.filter(involvesViewer);
  };

  const handleRowClick = (event, item, ctx) => {
    // ignore clicks from portalled content (modals opened from the row menu)
    // and from the row's own interactive controls
    if (!event.currentTarget.contains(event.target)) return;
    if (event.target.closest('a, button, input, .dropdown')) return;
    const href = getRowHref(item, ctx);
    if (!href) return;
    if (openInNewTab) {
      window.open(href, '_blank', 'noopener');
      return;
    }
    navigate(href);
  };

  const renderRow = (item, level, rowClass) => {
    const ctx = { level };
    const canToggle = hasHiddenByDefault(item);
    const isExpanded = expandedIds.has(item.id);

    return (
      <Table.Row
        key={`activity_row_${item.id}`}
        className={`activity-list-table__row activity-list-table__row--level-${Math.min(
          level,
          3
        )} ${rowClass}${
          getRowHref(item, ctx) ? '' : ' activity-list-table__row--static'
        }${getRowClassName ? ` ${getRowClassName(item, ctx)}` : ''}`}
        onClick={(event) => handleRowClick(event, item, ctx)}
      >
        {columns.map((column, c) => (
          <Table.Cell
            key={`activity_row_${item.id}_cell_${c}`}
            width={column.width || null}
            className={column.className || ''}
          >
            {c === 0 ? (
              <div className="activity-list-table__first-cell">
                {canToggle ? (
                  <button
                    type="button"
                    className="activity-list-table__toggle"
                    aria-expanded={isExpanded}
                    aria-label={t(
                      isExpanded
                        ? 'activity.home.program_list.hide_linked'
                        : 'activity.home.program_list.show_linked',
                      { name: item.name }
                    )}
                    onClick={() => toggleExpanded(item.id)}
                  >
                    <Icon name={isExpanded ? 'angle down' : 'angle right'} />
                  </button>
                ) : null}
                {column.render(item, ctx)}
              </div>
            ) : (
              column.render(item, ctx)
            )}
          </Table.Cell>
        ))}
      </Table.Row>
    );
  };

  const renderLinked = (program, level) =>
    visibleLinked(program).map((child) => (
      <Fragment key={`activity_linked_${child.id}`}>
        {renderRow(child, level, '')}
        {hasLinkedActivities(child) ? renderLinked(child, level + 1) : null}
      </Fragment>
    ));

  const blocks = [];
  data.forEach((item) => {
    const last = blocks[blocks.length - 1];
    if (hasLinkedActivities(item)) {
      blocks.push({ type: 'program', item });
    } else if (last && last.type === 'plain') {
      last.items.push(item);
    } else {
      blocks.push({ type: 'plain', items: [item] });
    }
  });

  return (
    <Table
      basic="very"
      stackable
      className="home-activity tbody-hover activity-list-table"
    >
      <Table.Header fullWidth>
        <Table.Row>
          {columns.map((column, i) => (
            <Table.HeaderCell
              key={`activity_list_header_${i}`}
              width={column.width || null}
              className={column.className || ''}
            >
              {column.title || ''}
            </Table.HeaderCell>
          ))}
        </Table.Row>
      </Table.Header>

      {blocks.map((block, b) => (
        <Fragment key={`activity_block_${b}`}>
          {block.type === 'plain' ? (
            <Table.Body>
              {block.items.map((item) => renderRow(item, 0, ''))}
            </Table.Body>
          ) : (
            <Table.Body
              className={`activity-list-table__program activity-list-table__program--${getActivityStatus(
                block.item.statusId
              )}`}
            >
              {renderRow(block.item, 0, 'activity-list-table__row--program')}
              {renderLinked(block.item, 1)}
            </Table.Body>
          )}
        </Fragment>
      ))}

      <Table.Footer>
        <Table.Row textAlign="center">
          <Table.HeaderCell colSpan={columns.length}>
            <Pagination
              className="table-pagination"
              size="tiny"
              activePage={page || 1}
              onPageChange={(e, { activePage }) => onPageChange(activePage)}
              totalPages={noOfPages || 1}
              pointing
              secondary
              firstItem={null}
              lastItem={null}
              siblingRange={1}
              stackable
            />
          </Table.HeaderCell>
        </Table.Row>
      </Table.Footer>
    </Table>
  );
};

export default ActivityListTable;
